import * as React from "react";
import {useState} from "react";
import {Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography,} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as XLSX from "xlsx";
import "../../../styles/rosterUpload.css";
import {api} from "../../../../../utils/axios";

interface RosterTerminateProps {
    clientId: string;
}

const REQUIRED_FIELDS = ["email", "phone"];

const RosterTerminate: React.FC<RosterTerminateProps> = ({ clientId }) => {
    const [file, setFile] = useState<File | null>(null);
    const [columns, setColumns] = useState<string[]>([]);
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [rawData, setRawData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [resultMessage, setResultMessage] = useState<string | null>(null);
    const [apiLoading, setApiLoading] = useState<boolean>(false);

    const allFieldsMapped = REQUIRED_FIELDS.every((field) => mapping[field]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const uploaded = e.target.files?.[0];
        if (!uploaded) return;

        setFile(uploaded);
        setLoading(true);

        try {
            const data = await uploaded.arrayBuffer();
            const workbook = XLSX.read(data, { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const parsed = XLSX.utils.sheet_to_json(sheet, { defval: "" });

            const headers = Object.keys(parsed[0] || {});
            setColumns(headers);
            setRawData(parsed);
        } catch (err) {
            console.error("Error parsing termination file:", err);
            setColumns([]);
            setRawData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleMappingChange = (field: string, value: string) => {
        setMapping((prev) => ({ ...prev, [field]: value }));
    };

    const handleTerminate = async () => {
        setApiLoading(true);
        try {
            const payload = {
                client_id: clientId,
                data: rawData.map((row) => ({
                    email: row[mapping["email"]],
                    phone: row[mapping["phone"]],
                })),
            };

            const response = await api.post("/admin/rosters/terminate-employees", payload);
            setResultMessage(`✅ ${response.data.message}`);
        } catch (err) {
            console.error("Termination failed", err);
            setResultMessage("❌ Termination failed");
        } finally {
            setApiLoading(false);
        }
    };

    return (
        <Box className="roster-upload-container">
            <Typography variant="h5" gutterBottom>
                Terminate Employees
            </Typography>

            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                Upload CSV/XLS/XLSX
                <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} />
            </Button>

            {loading && <CircularProgress sx={{ mt: 2 }} />}

            {file && !loading && (
                <>
                    <Typography sx={{ mt: 2 }}>Selected File: {file.name}</Typography>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6">Map Columns</Typography>
                        {REQUIRED_FIELDS.map((field) => (
                            <FormControl fullWidth sx={{ mt: 2 }} key={field}>
                                <InputLabel>{field.toUpperCase()}</InputLabel>
                                <Select
                                    value={mapping[field] || ""}
                                    onChange={(e) => handleMappingChange(field, e.target.value)}
                                    label={field}
                                >
                                    {columns.map((col) => (
                                        <MenuItem key={col} value={col}>
                                            {col}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ))}
                    </Box>

                    {allFieldsMapped && rawData.length > 0 && (
                        <Button
                            sx={{ mt: 4 }}
                            variant="contained"
                            color="error"
                            onClick={handleTerminate}
                        >
                            Terminate Employees
                        </Button>
                    )}

                    {apiLoading && (
                        <Box sx={{ mt: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Processing...
                            </Typography>
                        </Box>
                    )}

                    {resultMessage && (
                        <Typography sx={{ mt: 2 }} color="primary">
                            {resultMessage}
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
};

export default RosterTerminate;
